"use client";

import { useAuth } from "@clerk/nextjs";
import { logger } from "@trigger.dev/sdk/v3";
import { EyeOff, Play, Plus, Undo2 } from "lucide-react";
import { useTransition } from "react";
import { Button } from "~/components/ui/button";
import { useToast } from "~/hooks/use-toast";
import { useHashParams } from "~/hooks/useHashParams";
import { type SelectTranscript } from "~/server/db/transcripts/schema";
import { useTrackLemmas } from "~/state/lemmas/hooks/useTrackLemmas";

export function ControlButtons({
  lemmaId,
  isStandalone = false,
  transcript,
  reverseControls = false,
}: {
  lemmaId: number;
  isStandalone?: boolean;
  reverseControls?: boolean;
  transcript?: SelectTranscript;
}) {
  const { isSignedIn } = useAuth();

  const { isLemmaTracked, toggleTrackedLemma } = useTrackLemmas();
  const isKnown = isLemmaTracked(lemmaId);

  const [isPending, startTransition] = useTransition();
  const { setHashParams } = useHashParams();
  const { toast } = useToast();

  const handleSelectTranscript = () => {
    if (!transcript) return;
    setHashParams({
      start: transcript.start.toString(),
      duration: transcript.duration.toString(),
      playTiming: new Date().getTime().toString(), // this is needed to force plays when the seek time doesn't change
    });
  };

  const handleAddKnownLemma = () => {
    startTransition(() => {
      toggleTrackedLemma(lemmaId).catch((error) => {
        logger.error("Failed to toggle known lemma", { error });
        toast({
          title: "Acks!",
          description: "Failed to toggle known word",
        });
      });
    });
  };

  // these are reversed because removing my known words means making visible
  const RemoveIcon = reverseControls ? Plus : Undo2;
  const AddIcon = reverseControls ? Undo2 : EyeOff;

  const removeTooltip = reverseControls
    ? "Undo, add back to known words"
    : "Show, by removing from known words";
  const addTooltip = reverseControls
    ? "Remove from known words"
    : "Hide, by adding to known words";

  return (
    <>
      {isSignedIn && (
        <form action={handleAddKnownLemma}>
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            className="h-8 gap-1"
            onClick={(e) => e.stopPropagation()}
            title={isKnown ? removeTooltip : addTooltip}
          >
            {isKnown ? (
              <RemoveIcon className="h-3.5 w-3.5" />
            ) : (
              <AddIcon className="h-3.5 w-3.5" />
            )}
          </Button>
          <input type="hidden" name="lemmaId" value={lemmaId} />
        </form>
      )}
      {!isStandalone && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectTranscript();
          }}
        >
          <Play className="h-3.5 w-3.5" />
        </Button>
      )}
    </>
  );
}
